/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React from 'react';
import { toast } from 'sonner';
import { Palette, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BRAND_PRESETS } from '@/lib/brandPresets';

/**
 * BrandPalettesCard — curated preset palettes + user-saved custom palettes.
 * Extracted from BrandSettingsPage.jsx (Code Quality refactor).
 *
 * @param {object} settings  current brand settings (primaryColor, primaryColorAlt, accentColor, customPalettes)
 * @param {function} patch   partial updater: patch({ primaryColor, ... })
 */
export const BrandPalettesCard = ({ settings, patch }) => {
  const isActivePalette = (p) =>
    (settings.primaryColor || '').toLowerCase() === (p.primaryColor || '').toLowerCase() &&
    (settings.primaryColorAlt || '').toLowerCase() === (p.primaryColorAlt || '').toLowerCase();

  const applyPalette = (p) => {
    patch({ primaryColor: p.primaryColor, primaryColorAlt: p.primaryColorAlt, accentColor: p.accentColor });
    toast.success(`Palette "${p.label}" applicata. Ricorda di salvare.`);
  };

  const saveCurrentPalette = () => {
    const label = window.prompt('Nome della palette personalizzata (max 60 caratteri):', 'La mia palette');
    if (!label) return;
    const trimmed = label.trim().slice(0, 60);
    if (!trimmed) return;
    const existing = settings.customPalettes || [];
    if (existing.length >= 20) {
      toast.error('Massimo 20 palette personalizzate salvabili');
      return;
    }
    const newPalette = {
      id: `custom_${Date.now()}`,
      label: trimmed,
      primaryColor: settings.primaryColor,
      primaryColorAlt: settings.primaryColorAlt,
      accentColor: settings.accentColor,
    };
    patch({ customPalettes: [...existing, newPalette] });
    toast.success(`Palette "${trimmed}" aggiunta. Ricorda di salvare.`);
  };

  const deleteCustomPalette = (cp) => {
    if (!window.confirm(`Eliminare la palette "${cp.label}"?`)) return;
    patch({ customPalettes: (settings.customPalettes || []).filter((x) => x.id !== cp.id) });
    toast.info(`Palette "${cp.label}" rimossa. Ricorda di salvare.`);
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Palette className="h-4 w-4 text-primary" />
              Palette pronte
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Applica con un click una palette curata o una tua palette personalizzata salvata.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={saveCurrentPalette} data-testid="brand-save-palette-btn">
            <Plus className="h-4 w-4 mr-1" /> Salva palette attuale
          </Button>
        </div>
      </CardHeader>
      <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="brand-presets">
        {BRAND_PRESETS.map((preset) => {
          const Icon = preset.icon;
          const isActive = isActivePalette(preset);
          return (
            <button
              key={preset.id}
              type="button"
              onClick={() => applyPalette(preset)}
              className={[
                'group text-left rounded-xl border p-3 transition-all',
                'hover:shadow-elev-md hover:-translate-y-0.5',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
                isActive ? 'border-primary bg-primary/5 shadow-elev-sm' : 'border-border bg-card',
              ].join(' ')}
              data-testid={`brand-preset-${preset.id}`}
              aria-pressed={isActive}
            >
              <div
                className="h-14 rounded-lg mb-2 relative overflow-hidden flex items-end justify-end p-1.5"
                style={{ background: `linear-gradient(135deg, ${preset.primaryColor} 0%, ${preset.primaryColorAlt} 100%)` }}
                aria-hidden="true"
              >
                <span className="inline-block h-4 w-4 rounded-full border-2 border-white shadow" style={{ background: preset.accentColor }} />
                <Icon className="absolute top-1.5 left-1.5 h-4 w-4 text-white/90" />
              </div>
              <div className="flex items-center gap-1.5">
                <div className="font-semibold text-sm">{preset.label}</div>
                {isActive && (
                  <span className="text-[10px] font-bold uppercase tracking-wider text-primary">Attiva</span>
                )}
              </div>
              <div className="text-[11px] text-muted-foreground leading-snug mt-0.5 line-clamp-2">
                {preset.description}
              </div>
              <div className="flex items-center gap-1 mt-2">
                <span className="h-3 w-3 rounded-sm border border-border" style={{ background: preset.primaryColor }} title={preset.primaryColor} />
                <span className="h-3 w-3 rounded-sm border border-border" style={{ background: preset.primaryColorAlt }} title={preset.primaryColorAlt} />
                <span className="h-3 w-3 rounded-sm border border-border" style={{ background: preset.accentColor }} title={preset.accentColor} />
                <span className="ml-auto text-[10px] font-mono text-muted-foreground">{preset.primaryColor}</span>
              </div>
            </button>
          );
        })}

        {(settings.customPalettes || []).map((cp) => {
          const isActive = isActivePalette(cp);
          return (
            <div
              key={cp.id}
              className={[
                'relative group text-left rounded-xl border p-3 transition-all',
                'hover:shadow-elev-md hover:-translate-y-0.5',
                isActive ? 'border-primary bg-primary/5 shadow-elev-sm' : 'border-border border-dashed bg-card',
              ].join(' ')}
              data-testid={`brand-preset-custom-${cp.id}`}
            >
              <button
                type="button"
                onClick={() => applyPalette(cp)}
                className="block w-full text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-lg"
                aria-pressed={isActive}
              >
                <div
                  className="h-14 rounded-lg mb-2 relative overflow-hidden flex items-end justify-end p-1.5"
                  style={{ background: `linear-gradient(135deg, ${cp.primaryColor} 0%, ${cp.primaryColorAlt} 100%)` }}
                >
                  <span className="inline-block h-4 w-4 rounded-full border-2 border-white shadow" style={{ background: cp.accentColor }} />
                  <span className="absolute top-1.5 left-1.5 text-[9px] font-bold uppercase tracking-wider bg-white/25 text-white px-1 py-0.5 rounded">
                    Custom
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="font-semibold text-sm truncate">{cp.label}</div>
                  {isActive && (
                    <span className="text-[10px] font-bold uppercase tracking-wider text-primary">Attiva</span>
                  )}
                </div>
                <div className="flex items-center gap-1 mt-1">
                  <span className="h-3 w-3 rounded-sm border border-border" style={{ background: cp.primaryColor }} title={cp.primaryColor} />
                  <span className="h-3 w-3 rounded-sm border border-border" style={{ background: cp.primaryColorAlt }} title={cp.primaryColorAlt} />
                  <span className="h-3 w-3 rounded-sm border border-border" style={{ background: cp.accentColor }} title={cp.accentColor} />
                  <span className="ml-auto text-[10px] font-mono text-muted-foreground truncate">{cp.primaryColor}</span>
                </div>
              </button>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Elimina palette personalizzata"
                className="absolute top-1.5 right-1.5 h-6 w-6 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity bg-background/80"
                onClick={(e) => { e.stopPropagation(); deleteCustomPalette(cp); }}
                data-testid={`brand-preset-custom-delete-${cp.id}`}
              >
                <Trash2 className="h-3 w-3 text-destructive" />
              </Button>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};
